import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.circle_member || data.role === "admin" ? "/circle/dashboard" : "/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Sign In | Dispatch Hunts</title></Helmet>
      <section className="min-h-screen bg-carbon grain flex items-center justify-center px-4 pt-24 pb-16" data-testid="login-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <p className="text-label text-brass mb-3">Member Access</p>
            <h1 className="font-display text-3xl font-bold text-bone">Sign in to your account</h1>
          </div>

          <form onSubmit={handleSubmit} className="bg-chalk p-8 space-y-6" data-testid="login-form">
            {error && <div className="bg-copper/10 border border-copper/30 text-copper text-sm font-body p-3" data-testid="login-error">{error}</div>}

            <div>
              <label className="font-label text-xs font-semibold tracking-wider uppercase text-carbon block mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full h-12 px-4 bg-bone border border-carbon/20 font-body text-sm text-carbon focus:outline-none focus:border-brass"
                data-testid="login-email-input" />
            </div>

            <div>
              <label className="font-label text-xs font-semibold tracking-wider uppercase text-carbon block mb-2">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full h-12 px-4 pr-12 bg-bone border border-carbon/20 font-body text-sm text-carbon focus:outline-none focus:border-brass"
                  data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate/50">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-brass w-full justify-center" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"} <ArrowRight className="w-4 h-4" />
            </button>

            <div className="text-center space-y-2">
              <Link to="/forgot-password" className="font-body text-sm text-brass hover:underline">Forgot password?</Link>
              <p className="font-body text-sm text-slate">
                Don't have an account? <Link to="/register" className="text-brass hover:underline" data-testid="login-register-link">Create one</Link>
              </p>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
